// Fixture payloads for the mock submission-flow API.
// Closing dates are computed RELATIVE to "now" at request time so demos never
// rot — the "Closes in 12 days" breakdown always closes in 12 days.

import {
  IAccountDefaultsDto,
  IAgencyOptionDto,
  IAgentOptionDto,
  IBreakdownCityDto,
  IBreakdownRoleDto,
  IBreakdownSubmissionInfoDto,
  ICastingCallDto,
  ICastingCallListDto,
  IPaywallOfferDto,
  IPaywallServiceDto,
  ISubmissionGeoGuessDto,
  Step4Variant,
} from "./types";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const iso = (ms: number) => new Date(ms).toISOString();

// Expiry is day-granular: a breakdown closes at the end of its closing day.
const startOfDay = (ms: number) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// Well-known ids the mock honors regardless of the active scenario, so demo
// bookmarks stay stable (documented in README.md next to this file).
// Refs are zero-padded strings, exactly as they arrive on a real ad link
// (bd=0175445). Kept as strings end to end so the padding survives — nothing
// in the flow does arithmetic on them.
export const MOCK_IDS = {
  openBreakdown: "0175445",
  openRole: "504827",
  closingTodayBreakdown: "0175446",
  closedBreakdown: "0175400",
  roleCastBreakdown: "0175422",
  // Still running, every role cast: closed, but a different closed from an
  // expired one (closedReason "allRolesCast").
  allCastBreakdown: "0175423",
  notFoundBreakdown: "0000000",
  castRole: "504800",
  // Casts in Vancouver AND Los Angeles, so the country gate clears for a
  // visitor in either country. This is the id to walk the CA-vs-US difference:
  // same breakdown, same roles, but the trial prompt and the reassurance copy
  // change with the visitor's country (US submits free, everyone else is
  // offered the 30-day trial).
  crossBorderBreakdown: "0175460",
  crossBorderRole: "504840",
  // Deadline passed yesterday, status still "Open": inside the late margin
  // (LATE_SUBMIT_DAYS), so it still takes submissions and every screen says
  // it has expired.
  staleDeadlineBreakdown: "0175470",
  staleDeadlineRole: "504870",
  // Deadline 20 days ago: past the late margin, so missed. Grey in the list,
  // the closed page from a link.
  missedBreakdown: "0175471",
};

export const VANCOUVER: IBreakdownCityDto = {
  cityId: 1234,
  cityName: "Vancouver",
  provinceId: 2,
  countryId: 38,
  countryCode: "CA",
  regionCode: "BC",
};

export const MEXICO_CITY: IBreakdownCityDto = {
  cityId: 5678,
  cityName: "Mexico City",
  provinceId: 91,
  countryId: 484,
  countryCode: "MX",
  regionCode: "CMX",
};

export const LOS_ANGELES: IBreakdownCityDto = {
  cityId: 9001,
  cityName: "Los Angeles",
  provinceId: 105,
  countryId: 840,
  countryCode: "US",
  // Note the collision the flow has to survive: region "CA" (California) under
  // country "US". Anything comparing the two fields as if they were the same
  // vocabulary gets this wrong.
  regionCode: "CA",
};

// Options for the home-city picker behind the country gate. Deliberately spans
// several countries: the point of the picker is that a visitor whose IP says
// Mexico may actually live in Vancouver, and the gate has to clear when they
// say so.
export const CITY_OPTIONS: IBreakdownCityDto[] = [
  VANCOUVER,
  { cityId: 1235, cityName: "Victoria", provinceId: 2, countryId: 38, countryCode: "CA", regionCode: "BC" },
  { cityId: 1111, cityName: "Toronto", provinceId: 8, countryId: 38, countryCode: "CA", regionCode: "ON" },
  { cityId: 1112, cityName: "Montreal", provinceId: 5, countryId: 38, countryCode: "CA", regionCode: "QC" },
  MEXICO_CITY,
  { cityId: 5679, cityName: "Guadalajara", provinceId: 92, countryId: 484, countryCode: "MX", regionCode: "JAL" },
  LOS_ANGELES,
  { cityId: 9002, cityName: "New York", provinceId: 133, countryId: 840, countryCode: "US", regionCode: "NY" },
  { cityId: 4401, cityName: "London", provinceId: 210, countryId: 826, countryCode: "GB", regionCode: "LND" },
];

// The account's city at creation, resolved by the chain the backend will use:
// the geolocated city, else the first city in the same province, else the first
// city in the country. Returns the rung it landed on so the flow can tell a
// precise city from a country-level guess — a countryFallback city is a guess
// worth letting the user correct, an ipCity is not.
export const resolveAccountCity = (
  geo: { cityId?: number; regionCode?: string; countryCode?: string } | null,
  options: IBreakdownCityDto[] = CITY_OPTIONS
): { city?: IBreakdownCityDto; source: IAccountDefaultsDto["geoSource"] } => {
  const upper = (v?: string) => (v || "").trim().toUpperCase();
  if (!geo) return { source: "none" };

  if (geo.cityId) {
    const exact = options.filter((c) => c.cityId === geo.cityId)[0];
    if (exact) return { city: exact, source: "ipCity" };
  }

  const country = upper(geo.countryCode);
  const region = upper(geo.regionCode);

  // Province rung: only meaningful within the right country, because region
  // codes are not globally unique ("CA" is California AND Canada's country).
  if (country && region) {
    const inProvince = options.filter(
      (c) => upper(c.countryCode) === country && upper(c.regionCode) === region
    )[0];
    if (inProvince) return { city: inProvince, source: "provinceFallback" };
  }

  if (country) {
    const inCountry = options.filter((c) => upper(c.countryCode) === country)[0];
    if (inCountry) return { city: inCountry, source: "countryFallback" };
  }

  return { source: "none" };
};

const losSantosRoles: IBreakdownRoleDto[] = [
  { roleId: MOCK_IDS.openRole, roleName: "Detective Ray Mendez", status: "Open" },
  { roleId: "504828", roleName: "Ella (Lead)", status: "Open" },
  { roleId: MOCK_IDS.castRole, roleName: "Officer Danvers", status: "Cast" },
];

const withSelectedRole = (
  info: IBreakdownSubmissionInfoDto,
  roleId?: string
): IBreakdownSubmissionInfoDto => {
  if (!roleId) return info;
  const match = info.roles.filter((r) => r.roleId === roleId)[0];
  // Unknown roleId on a known breakdown: no selectedRole, as the real API
  // answers it (the flow sends the visitor to pick one of the breakdown's).
  return match ? { ...info, selectedRole: match } : info;
};

export const buildOpenBreakdown = (
  roleId?: string,
  now: number = Date.now()
): IBreakdownSubmissionInfoDto =>
  withSelectedRole(
    {
      breakdownId: MOCK_IDS.openBreakdown,
      breakdownName: "Feature Film - Los Santos",
      projectType: "Feature Film",
      status: "Open",
      closingDateUtc: iso(now + 12 * DAY_MS),
      serverTimeUtc: iso(now),
      cityIds: [VANCOUVER.cityId],
      cities: [VANCOUVER],
      roles: losSantosRoles,
      trialDays: 30,
    },
    roleId
  );

export const buildClosingTodayBreakdown = (
  roleId?: string,
  now: number = Date.now()
): IBreakdownSubmissionInfoDto =>
  withSelectedRole(
    {
      ...buildOpenBreakdown(undefined, now),
      breakdownId: MOCK_IDS.closingTodayBreakdown,
      breakdownName: "Commercial - Midnight Sun Energy",
      projectType: "Commercial",
      closingDateUtc: iso(now + 6 * HOUR_MS),
    },
    roleId
  );

// The CA-vs-US walkthrough. Cast in Vancouver AND Los Angeles so the country
// gate clears from either side: what changes between the two runs is the trial
// prompt and the reassurance copy, not access.
//
// Closing date is a whole number of days out because expiry is day-granular —
// a breakdown closes at the end of its closing day, never at an odd hour.
const crossBorderRoles: IBreakdownRoleDto[] = [
  { roleId: MOCK_IDS.crossBorderRole, roleName: "Nadia (Series Regular)", status: "Open" },
  { roleId: "504841", roleName: "Bilingual Detective", status: "Open" },
  { roleId: "504842", roleName: "Border Agent (Day Player)", status: "Open" },
];

export const buildCrossBorderBreakdown = (
  roleId?: string,
  now: number = Date.now()
): IBreakdownSubmissionInfoDto =>
  withSelectedRole(
    {
      breakdownId: MOCK_IDS.crossBorderBreakdown,
      breakdownName: "Streaming Series - Northern Line",
      projectType: "Streaming Series",
      status: "Open",
      closingDateUtc: iso(startOfDay(now + 5 * DAY_MS) + DAY_MS - 1),
      serverTimeUtc: iso(now),
      cityIds: [VANCOUVER.cityId, LOS_ANGELES.cityId],
      cities: [VANCOUVER, LOS_ANGELES],
      roles: crossBorderRoles,
      trialDays: 30,
    },
    roleId
  );

// The API's castingCalls.lateSubmitDays: after its deadline a casting call
// still takes submissions this long, labelled Expired. Same rule as the API
// (CastingCallsService.ShapeInfo): inside the margin Open with isLate, past it
// Closed / expired.
export const LATE_SUBMIT_DAYS = 15;

export const withLateRule = (
  info: IBreakdownSubmissionInfoDto,
  now: number = Date.now()
): IBreakdownSubmissionInfoDto => {
  if (info.status !== "Open") return { ...info, isLate: false };
  const closes = Date.parse(info.closingDateUtc);
  if (isNaN(closes) || closes > now) return { ...info, isLate: false };
  if (now < closes + LATE_SUBMIT_DAYS * DAY_MS) return { ...info, isLate: true };
  return { ...info, status: "Closed", closedReason: "expired", isLate: false };
};

const listFlags = (closingDateUtc: string, now: number) => {
  const closes = Date.parse(closingDateUtc);
  const isExpired = !isNaN(closes) && closes <= now;
  return { isExpired, isLate: isExpired && now < closes + LATE_SUBMIT_DAYS * DAY_MS };
};

// Expired, but the status still says "Open": the closing date was yesterday.
// Inside the late margin, so the flow takes it (withLateRule sets isLate) and
// says it has expired.
export const buildStaleDeadlineBreakdown = (
  roleId?: string,
  now: number = Date.now()
): IBreakdownSubmissionInfoDto =>
  withSelectedRole(
    {
      breakdownId: MOCK_IDS.staleDeadlineBreakdown,
      breakdownName: "Feature Film - Last Call",
      projectType: "Feature Film",
      // Deliberately still Open. That is the whole point of the fixture.
      status: "Open",
      // End of YESTERDAY — expiry is day-granular.
      closingDateUtc: iso(startOfDay(now) - 1),
      serverTimeUtc: iso(now),
      cityIds: [VANCOUVER.cityId],
      cities: [VANCOUVER],
      roles: [
        { roleId: MOCK_IDS.staleDeadlineRole, roleName: "Marguerite (Lead)", status: "Open" },
        { roleId: "504871", roleName: "Stage Manager", status: "Open" },
      ],
      trialDays: 30,
    },
    roleId
  );

export const buildClosedBreakdown = (
  roleId?: string,
  now: number = Date.now()
): IBreakdownSubmissionInfoDto =>
  withSelectedRole(
    {
      ...buildOpenBreakdown(undefined, now),
      breakdownId: MOCK_IDS.closedBreakdown,
      status: "Closed",
      closingDateUtc: iso(now - 1 * DAY_MS),
      roles: losSantosRoles.map((r) => ({ ...r, status: "Closed" as const })),
    },
    roleId
  );

export const buildAllCastBreakdown = (
  now: number = Date.now()
): IBreakdownSubmissionInfoDto => ({
  ...buildOpenBreakdown(undefined, now),
  breakdownId: MOCK_IDS.allCastBreakdown,
  breakdownName: "Feature Film - Harbour Lights",
  status: "Closed",
  closedReason: "allRolesCast",
  roles: losSantosRoles.map((r) => ({ ...r, status: "Cast" as const })),
});

// Breakdown still open, but the role the ad pointed at was cast mid-campaign.
// Given a roleId, it answers that role as the real API does (picking one of the
// open roles from the landing page goes on to step 1); without one, the cast role.
export const buildRoleCastBreakdown = (
  now: number = Date.now(),
  roleId?: string
): IBreakdownSubmissionInfoDto => {
  const info = { ...buildOpenBreakdown(undefined, now), breakdownId: MOCK_IDS.roleCastBreakdown };
  if (roleId) return withSelectedRole(info, roleId);
  return {
    ...info,
    selectedRole: { roleId: MOCK_IDS.castRole, roleName: "Officer Danvers", status: "Cast" },
  };
};

// ---------------------------------------------------------------------------
// Casting calls — the demo hub.
//
// Every scenario the flow has a wall for is reachable from this list, in the
// market it belongs to, so a walkthrough can start here instead of from a
// bookmarked breakdown id. The list is market-aware: a visitor in the US sees
// Los Angeles calls, one in Mexico sees Mexico City, and the cross-border job
// appears in BOTH, because it casts in both.
// ---------------------------------------------------------------------------

const VICTORIA: IBreakdownCityDto = {
  cityId: 1235, cityName: "Victoria", provinceId: 2, countryId: 38, countryCode: "CA", regionCode: "BC",
};
const MONTREAL: IBreakdownCityDto = {
  cityId: 1112, cityName: "Montreal", provinceId: 5, countryId: 38, countryCode: "CA", regionCode: "QC",
};
const NEW_YORK: IBreakdownCityDto = {
  cityId: 9002, cityName: "New York", provinceId: 133, countryId: 840, countryCode: "US", regionCode: "NY",
};
const GUADALAJARA: IBreakdownCityDto = {
  cityId: 5679, cityName: "Guadalajara", provinceId: 92, countryId: 484, countryCode: "MX", regionCode: "JAL",
};

const call = (
  breakdownId: string,
  breakdownName: string,
  projectType: string,
  city: IBreakdownCityDto,
  closingDateUtc: string,
  roles: IBreakdownRoleDto[],
  extra: Partial<ICastingCallDto> = {}
): ICastingCallDto => ({
  breakdownId,
  breakdownName,
  projectType,
  cityId: city.cityId,
  cityName: city.cityName,
  countryCode: city.countryCode || "",
  closingDateUtc,
  roles,
  status: "Open",
  cities: [city],
  ...extra,
});

// Day-granular expiry: a breakdown closes at the end of its closing day.
const endOfDayIn = (now: number, days: number) =>
  iso(startOfDay(now + days * DAY_MS) + DAY_MS - 1);

export const buildAllCastingCalls = (now: number = Date.now()): ICastingCallDto[] => [
  // -- Canada ---------------------------------------------------------------
  // The happy path. Two open roles and one already cast, so the card shows what
  // a partially-cast breakdown looks like.
  call(
    MOCK_IDS.openBreakdown,
    "Feature Film - Los Santos",
    "Feature Film",
    VANCOUVER,
    endOfDayIn(now, 12),
    losSantosRoles
  ),
  // Urgency: the pill counts in hours, then in minutes under the hour.
  call(
    MOCK_IDS.closingTodayBreakdown,
    "Commercial - Midnight Sun Energy",
    "Commercial",
    VANCOUVER,
    iso(now + 6 * HOUR_MS),
    [
      { roleId: MOCK_IDS.openRole, roleName: "Runner", status: "Open" },
      { roleId: "504902", roleName: "Barista (Featured)", status: "Open" },
    ]
  ),
  // Casts in two countries, so it appears in the CA list AND the US list and
  // the country gate clears from either side. This is the CA-vs-US walkthrough.
  call(
    MOCK_IDS.crossBorderBreakdown,
    "Streaming Series - Northern Line",
    "Streaming Series",
    VANCOUVER,
    endOfDayIn(now, 5),
    crossBorderRoles,
    { cities: [VANCOUVER, LOS_ANGELES] }
  ),
  // Breakdown open, every role cast — a different thing to say than "closed":
  // the project is still live, there is just nothing to submit to.
  call(
    MOCK_IDS.roleCastBreakdown,
    "Series - Harbour Line",
    "Television",
    VANCOUVER,
    endOfDayIn(now, 8),
    [
      { roleId: MOCK_IDS.castRole, roleName: "Officer Danvers", status: "Cast" },
      { roleId: "504801", roleName: "Desk Sergeant", status: "Cast" },
    ]
  ),
  // Closed outright: status and deadline agree. Greyed out, unclickable.
  call(
    MOCK_IDS.closedBreakdown,
    "Series - Northbound",
    "Television",
    VANCOUVER,
    iso(now - 2 * DAY_MS),
    [
      { roleId: "505201", roleName: "Sam", status: "Closed" },
      { roleId: "505202", roleName: "Robin", status: "Closed" },
    ],
    { status: "Closed" }
  ),
  // Deadline yesterday, status still "Open": expired but inside the late
  // margin, so an amber Expired card with its Submit buttons.
  call(
    MOCK_IDS.staleDeadlineBreakdown,
    "Feature Film - Last Call",
    "Feature Film",
    VANCOUVER,
    iso(startOfDay(now) - 1),
    [
      { roleId: MOCK_IDS.staleDeadlineRole, roleName: "Marguerite (Lead)", status: "Open" },
      { roleId: "504871", roleName: "Stage Manager", status: "Open" },
    ]
  ),
  // Deadline 20 days ago: past the late margin, missed. Grey, no button.
  call(
    MOCK_IDS.missedBreakdown,
    "Feature Film - Old News",
    "Feature Film",
    VANCOUVER,
    iso(startOfDay(now) - 20 * DAY_MS),
    [{ roleId: "504880", roleName: "Anchor", status: "Open" }]
  ),
  // Long, accented title: truncation and i18n rendering.
  call(
    "0175451",
    "Théâtre de la Reine — L'Affaire Mont-Royal, Deuxième Partie",
    "Theatre",
    MONTREAL,
    endOfDayIn(now, 21),
    [
      { roleId: "505301", roleName: "Geneviève", status: "Open" },
      { roleId: "505302", roleName: "L'Inspecteur", status: "Cast" },
    ]
  ),
  // A single-role card, so the grid has a short card next to the tall ones.
  call(
    "0175452",
    "Short Film - The Quiet Part",
    "Short Film",
    VICTORIA,
    endOfDayIn(now, 2),
    [{ roleId: "505401", roleName: "Jo (Lead)", status: "Open" }]
  ),

  // -- United States --------------------------------------------------------
  // The same cross-border job, matched on its US city.
  call(
    MOCK_IDS.crossBorderBreakdown,
    "Streaming Series - Northern Line",
    "Streaming Series",
    LOS_ANGELES,
    endOfDayIn(now, 5),
    crossBorderRoles,
    { cities: [VANCOUVER, LOS_ANGELES] }
  ),
  call(
    "0175480",
    "Pilot - Sunset & Vine",
    "Television",
    LOS_ANGELES,
    endOfDayIn(now, 9),
    [
      { roleId: "505501", roleName: "Marcus (Series Regular)", status: "Open" },
      { roleId: "505502", roleName: "Dana (Recurring)", status: "Open" },
      { roleId: "505503", roleName: "Studio Exec", status: "Cast" },
    ]
  ),
  call(
    "0175481",
    "National Commercial - Rally Sportswear",
    "Commercial",
    LOS_ANGELES,
    iso(now + 20 * HOUR_MS),
    [{ roleId: "505601", roleName: "Runner (Hero)", status: "Open" }]
  ),
  call(
    "0175482",
    "Off-Broadway - The Fifth Avenue Letters",
    "Theatre",
    NEW_YORK,
    endOfDayIn(now, 15),
    [
      { roleId: "505701", roleName: "Eleanor", status: "Open" },
      { roleId: "505702", roleName: "The Porter", status: "Open" },
    ]
  ),

  // -- Mexico ---------------------------------------------------------------
  // Where "Show me roles in Mexico City" lands from the country gate.
  call(
    "0175450",
    "Largometraje — Ángel de la Muerte: El Ajuste de Cuentas de González",
    "Feature Film",
    MEXICO_CITY,
    endOfDayIn(now, 3),
    [
      { roleId: "505101", roleName: "Ángel", status: "Open" },
      { roleId: "505102", roleName: "Comandante Ruiz", status: "Cast" },
    ]
  ),
  call(
    "0175490",
    "Telenovela - Las Dos Orillas",
    "Television",
    MEXICO_CITY,
    endOfDayIn(now, 11),
    [
      { roleId: "505801", roleName: "Valeria", status: "Open" },
      { roleId: "505802", roleName: "Don Emilio", status: "Open" },
    ]
  ),
  call(
    "0175491",
    "Serie Web - Guadalajara Nights",
    "Streaming Series",
    GUADALAJARA,
    endOfDayIn(now, 6),
    [{ roleId: "505901", roleName: "Mateo", status: "Open" }]
  ),
];

const marketLabel = (countryCode: string, cityId?: number): string => {
  if (cityId) {
    const city = CITY_OPTIONS.filter((c) => c.cityId === cityId)[0];
    if (city) return city.cityName;
  }
  const first = CITY_OPTIONS.filter(
    (c) => (c.countryCode || "").toUpperCase() === countryCode
  )[0];
  return first ? first.cityName : "";
};

// Filtered the way the real endpoint will be: by country, narrowed to a city
// when one is asked for. A market with no calls returns an EMPTY list rather
// than another market's — showing Vancouver roles to someone who asked for
// Mexico City is the exact bug the country gate exists to prevent.
// A breakdown from the casting-calls lists, as the submission flow sees it: the same title,
// cities, roles and deadline the card showed. Without this every listed id but the
// well-known ones echoed the Los Santos fixture, so a role picked from the Mexico City list
// read as "cast" and closed. Null for an id no list has.
export const buildListedBreakdown = (
  breakdownId: string,
  roleId?: string,
  now: number = Date.now()
): IBreakdownSubmissionInfoDto | null => {
  const listed = buildAllCastingCalls(now).filter((c) => c.breakdownId === breakdownId)[0];
  if (!listed) return null;
  const cities = listed.cities && listed.cities.length ? listed.cities : [];
  return withSelectedRole(
    {
      breakdownId: listed.breakdownId,
      breakdownName: listed.breakdownName,
      projectType: listed.projectType,
      status: listed.status || "Open",
      closingDateUtc: listed.closingDateUtc,
      serverTimeUtc: iso(now),
      cityIds: cities.map((c) => c.cityId),
      cities,
      roles: listed.roles,
      trialDays: 30,
    },
    roleId
  );
};

// Same policy as the API (CastingCallsSettings): CA/US/MX visitors may switch
// between those countries, except a Canadian, who gets Canada and the US. Keyed
// by the visitor's own country, not the one on screen.
const SWITCHABLE = ["CA", "US", "MX"];
const SWITCH_LIMITS: { [visitor: string]: string[] } = { CA: ["CA", "US"] };

export const selectableCountriesFor = (visitor: string, shown: string): string[] => {
  const v = (visitor || "").toUpperCase();
  const allowed =
    SWITCHABLE.indexOf(v) >= 0 ? SWITCHABLE.filter((c) => !SWITCH_LIMITS[v] || SWITCH_LIMITS[v].indexOf(c) >= 0) : [];
  return allowed.indexOf(shown) >= 0 ? allowed : [shown];
};

/** The country the API would list for a visitor asking for one: theirs, when the switch isn't allowed. */
export const listedCountryFor = (visitor: string, asked: string): string => {
  const v = (visitor || "CA").toUpperCase();
  const a = (asked || v).toUpperCase();
  return selectableCountriesFor(v, a).length > 1 || a === v ? a : v;
};

export const buildCastingCalls = (
  now: number = Date.now(),
  cityName?: string,
  countryCode: string = "CA",
  cityId?: number,
  visitorCountry?: string
): ICastingCallListDto => {
  const country = (countryCode || "CA").toUpperCase();
  const matches = buildAllCastingCalls(now).filter((c) => {
    if ((c.countryCode || "").toUpperCase() !== country) return false;
    return cityId ? c.cityId === cityId : true;
  });
  return {
    serverTimeUtc: iso(now),
    cityId,
    cityName: cityName || marketLabel(country, cityId),
    countryCode: country,
    selectableCountries: selectableCountriesFor(visitorCountry || country, country),
    // The fixture refs are already in the classic spelling (0175445), so the
    // mock's classicRef is the ref itself — the real API sends both.
    castingCalls: matches.map((c) => ({
      ...c,
      ...listFlags(c.closingDateUtc, now),
      classicRef: c.classicRef || c.breakdownId,
      isVerifiedDirector: c.isVerifiedDirector !== undefined ? c.isVerifiedDirector : true,
      isPaid: c.isPaid !== undefined ? c.isPaid : true,
    })),
  };
};

export const buildEmptyCastingCalls = (
  now: number = Date.now(),
  cityName: string = VANCOUVER.cityName,
  countryCode: string = "CA"
): ICastingCallListDto => ({
  serverTimeUtc: iso(now),
  cityName,
  countryCode,
  castingCalls: [],
});

// ---------------------------------------------------------------------------
// Step 4 — the paywall offer
//
// Order and wording are Figma 19.2's expanded list, and the first five are what
// frame 19 shows before "See all benefits". The UI takes the split from
// `collapsedBenefitCount` rather than slicing a hardcoded number, so a change
// here does not need a matching change there.
// ---------------------------------------------------------------------------

export const PAYWALL_BENEFITS = [
  "Worldwide Breakdown Submissions",
  "16 Headshots",
  "Unlimited Resumes",
  "myCV - Your digital calling card",
  "myPerformance - Insights",
  "Unlimited Self-Tapes",
  "Actor.App iOS & Android",
  "Personal Website (Unlimited Photos)",
  "5 Hours + Demo Reels",
  "Built in Demo Editor",
  "Unlimited Video Links",
  "Virtual Home-check for self taping",
  "Portfolio Pitching & Tracking",
];

const COLLAPSED_BENEFITS = 5;

// Priced per market, because changing country changes plans (docs/16). The
// monthly equivalent is computed here, once, so the two front-ends and the
// server cannot round it differently — Figma's "$8.25 /Monthly, paid annually
// ($99)" is 99/12 to 2dp.
interface IMarketPricing {
  currencyCode: string;
  currencySymbol: string;
  fractional: string;
  annual: number;
  monthly: number;
}

const MARKET_PRICING: { [countryCode: string]: IMarketPricing } = {
  US: { currencyCode: "USD", currencySymbol: "$", fractional: ".", annual: 99, monthly: 14.95 },
  CA: { currencyCode: "CAD", currencySymbol: "$", fractional: ".", annual: 129, monthly: 18.95 },
  MX: { currencyCode: "MXN", currencySymbol: "$", fractional: ".", annual: 1790, monthly: 249 },
  GB: { currencyCode: "GBP", currencySymbol: "£", fractional: ".", annual: 89, monthly: 12.95 },
  ES: { currencyCode: "EUR", currencySymbol: "€", fractional: ",", annual: 99, monthly: 14.95 },
};

const round2 = (value: number) => Math.round(value * 100) / 100;

export const TRIAL_DAYS = 30;

const buildPaywallServices = (countryCode: string): IPaywallServiceDto[] => {
  const market = MARKET_PRICING[(countryCode || "CA").toUpperCase()] || MARKET_PRICING.CA;
  const shared = {
    currencyCode: market.currencyCode,
    currencySymbol: market.currencySymbol,
    fractional: market.fractional,
    trialDurationDays: TRIAL_DAYS,
    trialDurationMonths: 1,
  };
  return [
    {
      ...shared,
      serviceId: 233,
      name: "PRO Annual",
      price: market.annual,
      durationMonths: 12,
      monthlyEquivalent: round2(market.annual / 12),
    },
    {
      ...shared,
      serviceId: 231,
      name: "PRO Monthly",
      price: market.monthly,
      durationMonths: 1,
      monthlyEquivalent: round2(market.monthly),
    },
  ];
};

export const buildPaywallOffer = (
  variant: Step4Variant,
  countryCode: string = "CA",
  now: number = Date.now()
): IPaywallOfferDto => {
  const services = buildPaywallServices(countryCode);
  return {
    variant,
    services,
    // Annual first everywhere except the markets that convert better on
    // monthly — the same rule React-Membership's paywall applies.
    defaultServiceId:
      ["MX", "ES"].indexOf((countryCode || "").toUpperCase()) >= 0
        ? services[1].serviceId
        : services[0].serviceId,
    benefits: PAYWALL_BENEFITS.slice(),
    collapsedBenefitCount: COLLAPSED_BENEFITS,
    trialEndsOnUtc: iso(now + TRIAL_DAYS * DAY_MS),
    ccRequired: true,
  };
};

// Agency search for the representation sheet. Deliberately includes two names
// that collide on "Northern" so type-ahead has something to disambiguate.
export const AGENCY_OPTIONS: IAgencyOptionDto[] = [
  { agencyId: 8801, agencyName: "Belik Talent Management", cityName: "Vancouver" },
  { agencyId: 8802, agencyName: "Northern Lights Talent", cityName: "Vancouver" },
  { agencyId: 8803, agencyName: "Northern Star Artists", cityName: "Toronto" },
  { agencyId: 8804, agencyName: "Pacific Coast Casting Partners", cityName: "Vancouver" },
  { agencyId: 8805, agencyName: "Maple Leaf Artist Group", cityName: "Toronto" },
  { agencyId: 8806, agencyName: "Sunset Boulevard Talent", cityName: "Los Angeles" },
  { agencyId: 8807, agencyName: "Agencia Estrella", cityName: "Mexico City", allowNewAgents: false },
];

// Each agency's agents, as ProfileManagement lists them (GET /AgencyAgent/{agencyId}).
export const AGENCY_AGENTS: { [agencyId: number]: IAgentOptionDto[] } = {
  8801: [
    { agentId: 99101, agentName: "Nicolas Belik" },
    { agentId: 99102, agentName: "Dana Ruiz" },
  ],
  8802: [{ agentId: 99201, agentName: "Priya Shah" }],
  8807: [{ agentId: 99701, agentName: "Lucía Ortega" }],
};

export const GEO_CA: ISubmissionGeoGuessDto = {
  ip: "24.114.0.1",
  countryCode: "CA",
  regionCode: "BC",
  cityId: VANCOUVER.cityId,
  cityName: "Vancouver",
};

export const GEO_MX: ISubmissionGeoGuessDto = {
  ip: "187.190.0.1",
  countryCode: "MX",
  regionCode: "CMX",
  cityId: MEXICO_CITY.cityId,
  cityName: "Mexico City",
};
